import { gsap } from 'gsap';

/**
 * Демо платёжного окна: загрузка по требованию.
 *
 * Само окно — Vue-бандл боевого шлюза (frontend/src/demo), и лендингу он не нужен, пока
 * демо не открыли: скрипт подключается при первом клике по [data-payment-demo], дальше
 * бандл уже в памяти и открывает окно сам. Адрес бандла отдаёт blade
 * (partials/payment-demo.blade.php) — вместе с метками времени и переводами шлюза.
 *
 * Чтобы клик открывал окно сразу, бандл подключается и выполняется заранее: в простое после
 * загрузки страницы (requestIdleCallback — первой отрисовке лендинга не мешает) и раньше, если
 * пользователь уже тянется к кнопке — наведение, касание, фокус. Там же создаётся
 * Vue-экземпляр демо (prepare), и клику остаётся только нарисовать окно. Раньше заранее
 * только скачивался файл, а Vue, полифиллы и приложение выполнялись по клику — это и была
 * задержка первого открытия.
 */
let loading = null;

function getBundleUrl() {
  return window.__speendDemoBundle || null;
}

function loadBundle() {
  if (window.SpeendPaymentDemo) return Promise.resolve(window.SpeendPaymentDemo);
  if (loading) return loading;

  const src = getBundleUrl();

  if (!src) return Promise.reject(new Error('payment demo bundle url is not set'));

  loading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    // Бандл выставляет window.SpeendPaymentDemo синхронно при выполнении.
    script.onload = () => {
      if (window.SpeendPaymentDemo) {
        resolve(window.SpeendPaymentDemo);
        return;
      }
      // Любой провал сбрасывает загрузку: иначе отклонённый промис оставался бы в loading,
      // и кнопка молчала бы до перезагрузки страницы.
      loading = null;
      script.remove();
      reject(new Error('payment demo bundle is broken'));
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('payment demo bundle failed to load'));
    };
    document.body.appendChild(script);
  });

  return loading;
}

// Подключить и подготовить заранее. Ошибка не важна: клик загрузит скрипт штатно.
function warmBundle() {
  loadBundle()
    .then(demo => demo.prepare && demo.prepare())
    .catch(() => {});
}

function warmWhenIdle() {
  const run = () => (window.requestIdleCallback ? window.requestIdleCallback(warmBundle, { timeout: 5000 }) : setTimeout(warmBundle, 2000));

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}

function openDemo(trigger) {
  if (trigger) trigger.setAttribute('aria-busy', 'true');

  loadBundle()
    .then(demo => demo.open())
    .catch(error => console.error('[payment-demo]', error))
    .then(() => {
      if (trigger) trigger.removeAttribute('aria-busy');
    });
}

// Пока демо открыто, gsap-анимации лендинга (карточки героя прямо за окном) стоят: любое
// движение под backdrop-filter Safari пересчитывает на каждом кадре. CSS-анимаций на главной
// за окном нет, а пауза их классом на html стоила пересчёта стилей всего лендинга на каждом
// открытии и закрытии — в Safari это были лаги на частых кликах, поэтому её нет.
function bindBackgroundPause() {
  document.addEventListener('speend-demo:open', () => gsap.globalTimeline.pause());
  document.addEventListener('speend-demo:close', () => gsap.globalTimeline.resume());
}

export function initPaymentDemo() {
  // Делегирование, а не обход узлов: ссылка на демо приходит из lang-файла внутрь
  // ответа FAQ, и на момент инициализации её может не быть в разметке.
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-payment-demo]');

    if (!trigger) return;

    event.preventDefault();
    openDemo(trigger);
  });

  const onIntent = event => {
    if (event.target.closest && event.target.closest('[data-payment-demo]')) warmBundle();
  };

  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);

  bindBackgroundPause();
  warmWhenIdle();
}
